import { ProductionAccordion, ProductionEditableRowsSection, ProductionField } from "../ProductionBlocks";
import { hasAnyText, hasRows, hasText } from "../bisDossierData";
import { productionFieldGridStyle, productionIncidentButtonStyle, productionInputStyle } from "../bisProductionStyles";

// Grille de champs simples : [libellé, valeur, (nouvelle valeur) => void, type?, style du champ?]
function FieldGrid({ fields }) {
    return (
        <div style={productionFieldGridStyle}>
            {fields.map(([label, value, onChange, type]) => (
                <ProductionField key={label} label={label}>
                    <input style={productionInputStyle} type={type} value={value || ""} onChange={e => onChange(e.target.value)} />
                </ProductionField>
            ))}
        </div>
    );
}

const plural = (count, word) => `${count} ${word}${count > 1 ? "s" : ""}`;

function launchBlock({ updateMeta, workspace }) {
    const meta = workspace.formData.meta;
    const keys = ["pharmacien", "lancementHeure", "nombreSachetsProduits", "tempsProduction", "nombrePatients", "patientControle"];
    return {
        key: "lancement",
        title: "Informations de lancement",
        complete: hasAnyText(keys.map(key => meta[key])),
        content: <FieldGrid fields={[
            ["Pharmacien", meta.pharmacien, value => updateMeta("pharmacien", value)],
            ["Heure lancement", meta.lancementHeure, value => updateMeta("lancementHeure", value), "time"],
            ["Sachets produits", meta.nombreSachetsProduits, value => updateMeta("nombreSachetsProduits", value)],
            ["Temps production", meta.tempsProduction, value => updateMeta("tempsProduction", value)],
            ["Nombre patients", meta.nombrePatients, value => updateMeta("nombrePatients", value)],
            ["Patient libération", meta.patientControle, value => updateMeta("patientControle", value)],
        ]} />,
    };
}

function identificationBlock({ updateMeta, workspace }) {
    const meta = workspace.formData.meta;
    return {
        key: "identification",
        title: "1. Identification",
        complete: hasAnyText([meta.ehpad, meta.dateEdition, meta.dateLotDebut, meta.dateLotFin, meta.numeroControlePharmacie]),
        content: (
            <div style={productionFieldGridStyle}>
                <ProductionField label="EHPAD">
                    <input style={productionInputStyle} value={meta.ehpad || workspace.activeDossier.ehpadNom || ""} onChange={e => updateMeta("ehpad", e.target.value)} />
                </ProductionField>
                <ProductionField label="Date d'edition">
                    <input style={productionInputStyle} type="date" value={meta.dateEdition || ""} onChange={e => updateMeta("dateEdition", e.target.value)} />
                </ProductionField>
                <ProductionField label="Reference du lot" style={{ gridColumn: "span 2" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "minmax(130px, 1fr) auto minmax(130px, 1fr)", gap: "8px", alignItems: "center", minWidth: 0 }}>
                        <input style={productionInputStyle} type="date" value={meta.dateLotDebut || ""} onChange={e => updateMeta("dateLotDebut", e.target.value)} />
                        <span style={{ color: "#475569", fontSize: "13px", fontWeight: 900 }}>au</span>
                        <input style={productionInputStyle} type="date" value={meta.dateLotFin || ""} onChange={e => updateMeta("dateLotFin", e.target.value)} />
                    </div>
                </ProductionField>
                <ProductionField label="N° controle pharmacie">
                    <input style={productionInputStyle} value={meta.numeroControlePharmacie || ""} onChange={e => updateMeta("numeroControlePharmacie", e.target.value)} />
                </ProductionField>
            </div>
        ),
    };
}

function robotBlock({ updateSection, workspace }) {
    const robot = workspace.formData.productionRobot;
    const set = key => value => updateSection("productionRobot", { [key]: value });
    return {
        key: "productionRobot",
        title: "2. Production robot",
        complete: hasAnyText(Object.values(robot || {})),
        content: <FieldGrid fields={[
            ["Producteur / Robot", robot.producteurRobot, set("producteurRobot")],
            ["Residents actifs (P-Dose)", robot.residentsActifsPDose, set("residentsActifsPDose")],
            ["Residents produits (ecran robot)", robot.residentsProduitsRobot, set("residentsProduitsRobot")],
            ["Residents livres (D-Dose)", robot.residentsLivresDDose || robot.residentsLivresODose, set("residentsLivresDDose")],
        ]} />,
    };
}

function conditionsBlock({ updateSection, workspace }) {
    const conditions = workspace.formData.conditionsProduction;
    const set = key => value => updateSection("conditionsProduction", { [key]: value });
    return {
        key: "conditionsProduction",
        title: "3. Conditions de production",
        complete: hasAnyText(Object.values(conditions || {})),
        content: <FieldGrid fields={[
            ["Heure de debut", conditions.heureDebut, set("heureDebut"), "time"],
            ["Heure de fin", conditions.heureFin, set("heureFin"), "time"],
            ["Temperature (15-25 °C)", conditions.temperature, set("temperature")],
            ["Hygrometrie (< 60 %)", conditions.hygrometrie, set("hygrometrie")],
        ]} />,
    };
}

function residentsBlock({ updateRows, workspace }) {
    const residents = workspace.formData.residents;
    return {
        key: "residents",
        title: "4. Residents a produire / reproduire",
        complete: hasRows(residents),
        countLabel: plural((residents || []).length, "ligne"),
        content: (
            <ProductionEditableRowsSection title="" rows={residents} onChange={rows => updateRows("residents", rows)} columns={[
                ["nomPrenom", "Nom et prenom", "textarea"],
                ["motif", "Motif", "textarea"],
                ["initiales", "Initiales (si reproduit)", "text"],
            ]} />
        ),
    };
}

function incidentsBlock({ updateSection, workspace }) {
    const incidents = workspace.formData.incidents;
    return {
        key: "incidents",
        title: "5. Incidents de production",
        complete: hasText(incidents.problemes),
        content: (
            <div style={{ display: "grid", gap: "10px" }}>
                <div>
                    <div style={{ color: "#334155", fontSize: "12px", fontWeight: 800, marginBottom: "6px" }}>Problemes rencontres lors de la production ?</div>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        <button type="button" onClick={() => updateSection("incidents", { problemes: "OUI" })} style={productionIncidentButtonStyle(incidents.problemes === "OUI", "#dc2626")}>OUI</button>
                        <button type="button" onClick={() => updateSection("incidents", { problemes: "NON" })} style={productionIncidentButtonStyle(incidents.problemes === "NON", "#16a34a")}>NON</button>
                    </div>
                </div>
                {incidents.problemes === "OUI" && (
                    <ProductionField label="Si oui, detailler">
                        <textarea style={{ ...productionInputStyle, minHeight: "80px", resize: "vertical" }} value={incidents.details || ""} onChange={e => updateSection("incidents", { details: e.target.value })} />
                    </ProductionField>
                )}
            </div>
        ),
    };
}

function ipcBlock({ updateRows, updateSection, workspace }) {
    const { controlesQualite, controlesQualiteMeta } = workspace.formData;
    return {
        key: "ipc",
        title: "6. IPC - IN PROCESS CONTROL (1 controle tous les 10 rouleaux)",
        complete: hasRows(controlesQualite) || hasText(controlesQualiteMeta.rouleauxCyclamed),
        countLabel: plural((controlesQualite || []).length, "contrôle"),
        content: (
            <ProductionEditableRowsSection title="" rows={controlesQualite} onChange={rows => updateRows("controlesQualite", rows)} columns={[
                ["patientControle", "Nom et prenom du patient controle", "textarea"],
                ["ipcNumero", "IPC numero", "text"],
            ]} showIndex={false} footer={(
                <ProductionField label="Rouleaux patient mis a CYCLAMED">
                    <input style={productionInputStyle} value={controlesQualiteMeta.rouleauxCyclamed || ""} onChange={e => updateSection("controlesQualiteMeta", { rouleauxCyclamed: e.target.value })} />
                </ProductionField>
            )} />
        ),
    };
}

function releaseBlock({ updateSection, workspace }) {
    const release = workspace.formData.liberationLotProduction;
    const set = key => value => updateSection("liberationLotProduction", { [key]: value });
    return {
        key: "liberationLotProduction",
        title: "7. Libération de lot",
        complete: hasAnyText(Object.values(release || {})),
        content: <FieldGrid fields={[
            ["Nom du pharmacien", release.pharmacien, set("pharmacien")],
            ["Date", release.date, set("date"), "date"],
            ["Heure", release.heure, set("heure"), "time"],
            ["Signature et cachet", release.signatureCachet, set("signatureCachet")],
        ]} />,
    };
}

export function renderProductionDossierLotTab(view) {
    const blocks = [launchBlock, identificationBlock, robotBlock, conditionsBlock, residentsBlock, incidentsBlock, ipcBlock, releaseBlock]
        .map(build => build(view));
    return (
        <ProductionAccordion
            label="Progression dossier de lot"
            blocks={blocks}
            activeKey={view.activeProductionBlockKey}
            onActiveKeyChange={view.setActiveProductionBlockKey}
        />
    );
}
